import { useCallback, useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { ClickableQrCode } from "@/components/ui/ClickableQrCode";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { getGdMobileUploadQr } from "@/lib/dokumente";
import { DOCUMENT_EDITOR_OVERLAY_Z } from "@/pages/dokumente/documentEditorLayers";

interface DocumentMobileUploadDialogProps {
  open: boolean;
  onClose: () => void;
  docId: number | null;
  onUploaded?: () => void;
  zIndexClass?: string;
}

export function DocumentMobileUploadDialog({
  open,
  onClose,
  docId,
  onUploaded,
  zIndexClass = DOCUMENT_EDITOR_OVERLAY_Z,
}: DocumentMobileUploadDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [positionCount, setPositionCount] = useState<number | null>(null);

  const loadQr = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getGdMobileUploadQr(docId);
      setQrUri(data.qr_data_uri || null);
      setQrUrl(data.url || null);
      setExpiresAt(data.expires_at || null);
      setPositionCount(data.position_count ?? null);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "QR-Code konnte nicht erzeugt werden.",
      );
      setQrUri(null);
      setQrUrl(null);
    } finally {
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => {
    if (!open || !docId) return;
    void loadQr();
  }, [open, docId, loadQr]);

  useEffect(() => {
    if (!open || !docId || !onUploaded) return;
    const timer = window.setInterval(() => onUploaded(), 2500);
    return () => window.clearInterval(timer);
  }, [open, docId, onUploaded]);

  const expiresLabel = expiresAt
    ? new Date(expiresAt).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Positionsbilder vom Handy"
      description="QR-Code scannen – auf dem Handy siehst du die Angebotspositionen und kannst je Position Fotos hochladen."
      size="md"
      zIndexClass={zIndexClass}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8">
            <Spinner />
            <span className="text-sm text-label-secondary">QR-Code wird erzeugt …</span>
          </div>
        ) : error ? (
          <p className="text-sm text-status-red">{error}</p>
        ) : (
          <>
            {qrUri ? (
              <div className="flex justify-center">
                <ClickableQrCode
                  src={qrUri}
                  href={qrUrl}
                  alt="QR-Code für Handy-Upload"
                  className="h-56 w-56 rounded-lg bg-white p-2"
                />
              </div>
            ) : null}
            {positionCount != null ? (
              <p className="text-center text-sm text-label-secondary">
                {positionCount} Position{positionCount === 1 ? "" : "en"} auf dem Handy verfügbar
              </p>
            ) : null}
            {expiresLabel ? (
              <p className="text-center text-xs text-label-tertiary">Gültig bis {expiresLabel}</p>
            ) : null}
            {qrUrl ? (
              <a
                href={qrUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all text-center text-xs text-accent"
              >
                {qrUrl}
              </a>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
}
